import {
  useParentAttributeDetailsQuery,
  useParentAttributeDeleteMutation,
  useParentAttributeUpdateMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { extractMutationErrors } from "@dashboard/misc";
import createMetadataUpdateHandler from "@dashboard/utils/handlers/metadataUpdateHandler";
import React from "react";
import { useIntl } from "react-intl";

import ParentAttributePage, { ParentAttributePageFormData } from "../../components/ParentAttributePage";
import {
  ParentAttributeUrlQueryParams,
  parentAttributeListUrl,
  parentAttributeUrl,
} from "../../urls";

interface ParentAttributeDetailsProps {
  id: string;
  params: ParentAttributeUrlQueryParams;
}

const ParentAttributeDetails = ({ id, params }: ParentAttributeDetailsProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const { data, loading, refetch } = useParentAttributeDetailsQuery({
    variables: { id },
  });

  const [updateMetadata] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation({});

  const [deleteParentAttribute, deleteParentAttributeOpts] = useParentAttributeDeleteMutation({
    onCompleted: data => {
      if (data?.attributeDelete?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            id: "parentAttrDeleted",
            defaultMessage: "Parent attribute deleted",
          }),
        });
        navigate(parentAttributeListUrl());
      }
    },
  });

  const [updateParentAttribute, updateParentAttributeOpts] = useParentAttributeUpdateMutation({
    onCompleted: async data => {
      if (data?.attributeUpdate?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges),
        });
        // Refetch to get updated data including children
        await refetch();
      }
    },
  });

  const handleDelete = () => {
    deleteParentAttribute({ variables: { id } });
  };

  const handleUpdate = async (formData: ParentAttributePageFormData) => {
    return extractMutationErrors(
      updateParentAttribute({
        variables: {
          id,
          input: {
            name: formData.name,
            slug: formData.slug,
          },
        },
      }),
    );
  };

  const handleSubmit = createMetadataUpdateHandler(
    data?.attribute!,
    handleUpdate,
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables }),
  );

  const parentAttribute = data?.attribute;

  return (
    <ParentAttributePage
      parentAttribute={parentAttribute}
      disabled={loading || deleteParentAttributeOpts.loading || updateParentAttributeOpts.loading}
      errors={updateParentAttributeOpts?.data?.attributeUpdate?.errors || []}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      saveButtonBarState={updateParentAttributeOpts.status}
    />
  );
};

ParentAttributeDetails.displayName = "ParentAttributeDetails";

export default ParentAttributeDetails;